import type { SessionSettings } from '@/types/session';
import {
  DEFAULT_DISPLAY_DATE_FORMAT,
  DEFAULT_DISPLAY_DATETIME_FORMAT,
  DEFAULT_DISPLAY_TIME_FORMAT,
  normalizeDisplayDateFormat,
  normalizeDisplayDateTimeFormat,
  normalizeDisplayTimeFormat,
  type DisplayDateFormatId,
  type DisplayDateTimeFormatId,
  type DisplayTimeFormatId,
} from '@/utils/displayFormatConstants';
import { parseStoredUtcValue } from '@/utils/displayFormatEngine';

/** Display preferences from session (company timezone / locale / formats). */
export interface DisplayContext {
  timezone: string;
  locale: string;
  dateFormat: DisplayDateFormatId;
  timeFormat: DisplayTimeFormatId;
  dateTimeFormat: DisplayDateTimeFormatId;
}

const DEFAULT: DisplayContext = {
  timezone: 'UTC',
  locale: 'en-IN',
  dateFormat: DEFAULT_DISPLAY_DATE_FORMAT,
  timeFormat: DEFAULT_DISPLAY_TIME_FORMAT,
  dateTimeFormat: DEFAULT_DISPLAY_DATETIME_FORMAT,
};

let current: DisplayContext = { ...DEFAULT };

/** Apply session settings after bootstrap / company switch. */
export function applyDisplaySettings(settings?: SessionSettings | null): void {
  const activeTz = settings?.activeTimezone?.trim()
    || settings?.userTimezone?.trim()
    || settings?.timezone?.trim()
    || DEFAULT.timezone;
  current = {
    timezone: activeTz,
    locale: settings?.displayLocale?.trim() || settings?.locale?.trim() || DEFAULT.locale,
    dateFormat: normalizeDisplayDateFormat(settings?.dateFormat),
    timeFormat: normalizeDisplayTimeFormat(settings?.timeFormat),
    dateTimeFormat: normalizeDisplayDateTimeFormat(settings?.dateTimeFormat),
  };
}

/** Reset display context on logout (before next bootstrap). */
export function resetDisplaySettings(): void {
  current = { ...DEFAULT };
}

export function getDisplayTimezone(): string {
  return current.timezone;
}

export function getDisplayLocale(): string {
  return current.locale;
}

export function getDisplayDateFormat(): DisplayDateFormatId {
  return current.dateFormat;
}

export function getDisplayTimeFormat(): DisplayTimeFormatId {
  return current.timeFormat;
}

export function getDisplayDateTimeFormat(): DisplayDateTimeFormatId {
  return current.dateTimeFormat;
}

export function getDisplayContext(): DisplayContext {
  return { ...current };
}

/**
 * Parse API date/time strings as UTC instants.
 * Backend persists timestamps in UTC (naive ISO strings are treated as UTC).
 */
export function parseStoredUtc(value: string): Date {
  return parseStoredUtcValue(value);
}

/** Short timezone label for the active company (e.g. IST, GMT+5:30). */
export function formatTimezoneLabel(instant = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat(getDisplayLocale(), {
      timeZone: getDisplayTimezone(),
      timeZoneName: 'short',
    }).formatToParts(instant);
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? getDisplayTimezone();
  } catch {
    return getDisplayTimezone();
  }
}

function timezoneOffsetMs(instant: Date, timeZone: string): number {
  const map = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(instant)
      .map((p) => [p.type, p.value]),
  );
  const asUtc = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour),
    Number(map.minute),
    Number(map.second),
  );
  return asUtc - instant.getTime();
}

/**
 * Convert a company-local datetime picker value to UTC ISO string for API payloads.
 */
export function toUtcIsoStringFromCompanyLocal(localIso: string): string {
  const normalized = localIso.includes('T') ? localIso : `${localIso}T00:00:00`;
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(normalized.trim());
  if (!match) return new Date(localIso).toISOString();
  const [, y, mo, d, h, mi, s = '00'] = match;
  const wallUtc = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s));
  let offset = timezoneOffsetMs(new Date(wallUtc), getDisplayTimezone());
  let utc = wallUtc - offset;
  offset = timezoneOffsetMs(new Date(utc), getDisplayTimezone());
  utc = wallUtc - offset;
  return new Date(utc).toISOString();
}

/** Format Y-M-D parts as yyyy-MM-dd (timezone-neutral calendar date). */
export function calendarDateKeyFromParts(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * Calendar date key from a grid Date built via `new Date(y, m, d)` or date-fns.
 * Uses local Y-M-D components so browser vs company timezone cannot shift the day.
 */
export function calendarDateKeyFromWallDate(date: Date): string {
  return calendarDateKeyFromParts(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

/** Add calendar days to a yyyy-MM-dd key without timezone conversion. */
export function addCalendarDaysToKey(dateKey: string, days: number): string {
  const [y, mo, d] = dateKey.slice(0, 10).split('-').map(Number);
  return calendarDateKeyFromWallDate(new Date(y, mo - 1, d + days));
}

/** Calendar date key (yyyy-MM-dd) in the active display timezone. */
export function dateKeyInDisplayTimezone(instant: Date | string): string {
  const date = typeof instant === 'string' ? parseStoredUtcValue(instant) : instant;
  if (Number.isNaN(date.getTime())) {
    return typeof instant === 'string' && instant.length >= 10 ? instant.slice(0, 10) : '';
  }
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: getDisplayTimezone(),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/** Today's date (yyyy-MM-dd) in the active display timezone. */
export function todayInDisplayTimezone(): string {
  return dateKeyInDisplayTimezone(new Date());
}

/**
 * Convert UTC ISO from API to company-local picker value (yyyy-MM-dd'T'HH:mm).
 */
export function utcIsoToCompanyLocalPicker(iso: string | null | undefined): string {
  if (!iso?.trim()) return '';
  const date = parseStoredUtcValue(iso);
  if (Number.isNaN(date.getTime())) return iso.slice(0, 16);
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: getDisplayTimezone(),
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
      .formatToParts(date)
      .map((p) => [p.type, p.value]),
  );
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}
